import type { Header } from "./MergeCell"
import dayjs from "dayjs"
import { flatMap, isEqual, omit, omitBy } from "lodash-es"

// 切换地图的key
export const mapKey = import.meta.env.VITE_QQ_MAP_KEY

/** 格式化时间 */
export const formatDateTime = (datetime: string | number | Date) => {
  if (!datetime) {
    return "N/A"
  }
  return dayjs(datetime).format("YYYY-MM-DD HH:mm:ss")
}

/** 将全局 CSS 变量导入 JS 中使用 */
export const getCssVariableValue = (cssVariableName: string) => {
  let cssVariableValue = ""
  try {
    // 没有拿到值时，会返回空串
    cssVariableValue = getComputedStyle(document.documentElement).getPropertyValue(cssVariableName)
  } catch (error) {
    console.error(error)
  }
  return cssVariableValue
}

/** 获取增长率 */
export const growthRate = (x: number, y: number): number => {
  let growthRate: number
  if (y === 0) {
    growthRate = x > 0 ? 100 : 0
  } else {
    growthRate = Number((((x - y) / y) * 100).toFixed(2))
  }
  return growthRate
}

export const computedRateValue = (divideValue: number, dividendValue: number) => {
  if (dividendValue === 0) {
    return "0"
  } else if (divideValue === 0) {
    return "-100"
  }
  return (((divideValue - dividendValue) / dividendValue) * 100).toFixed(2)
}

/** 获取今日日期 */
export const getCurrentDate = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

type EnumType = { [key: string]: string | number }
type DescriptionType<T extends EnumType> = { [key in T[keyof T]]: string }

export function generateLabelValueArray<T extends EnumType>(
  enumType: T,
  descriptionType: DescriptionType<T>,
): { label: string; value: number }[] {
  return Object.entries(enumType)
    .filter(([, value]) => typeof value === "number") // 过滤出枚举值
    .map(([, value]) => ({
      label: descriptionType[value as T[keyof T]],
      value: value as number,
    }))
}

/**
 * 根据路径字符串获取嵌套对象的值
 * @param obj - 任意对象
 * @param path - 路径字符串，例如 "a.b.c"
 * @returns 嵌套对象的值
 */
export function getValueByPath(obj: any, path: string): any {
  if (path === "") return obj
  // 将路径字符串按 "." 分割成数组
  const keys = path.split(".")

  // 逐级获取嵌套对象的值
  return keys.reduce((acc, key) => {
    if (acc === null || acc === undefined) {
      return undefined
    }
    return acc[key]
  }, obj)
}

/**
 * 判断两个对象仅有指定的属性不同
 * @param obj1 对象1
 * @param obj2 对象2
 * @param property 属性
 * @returns 是否两个对象仅有指定的属性不同
 */
export function isOnlyOnePropertyDifferent<T>(obj1: T, obj2: T, property: keyof T): boolean {
  // 创建省略指定属性的新对象
  const obj1WithoutProperty = omit(obj1 as object, [property])
  const obj2WithoutProperty = omit(obj2 as object, [property])

  // 比较省略指定属性后的两个对象是否相等
  const areObjectsEqualWithoutProperty = isEqual(obj1WithoutProperty, obj2WithoutProperty)

  // 如果两个对象在省略指定属性后相等，且指定属性的值不相等，则返回 true
  return areObjectsEqualWithoutProperty && !isEqual(obj1[property], obj2[property])
}

export function flattenHeaders(headers: Header[], withParent = false): Header[] {
  return flatMap(headers, (header) => {
    const { children, ...rest } = header
    if (children) {
      if (withParent) {
        return [rest, ...flattenHeaders(children, withParent)]
      } else {
        return flattenHeaders(children, withParent)
      }
    } else {
      return [rest]
    }
  })
}

export function removeMatchingKeys(obj1: Record<string, any>, obj2: Record<string, any>) {
  const result = omitBy(obj1, (value, key) => isEqual(value, obj2[key]))
  return result
}

export function unionKeys(obj1: Record<string, any>, obj2: Record<string, any>) {
  const result = removeMatchingKeys(obj1, obj2)
  // 将obj2中不在obj1中的键值对添加到result中，值为空
  for (const key in obj2) {
    if (obj1[key] === undefined) {
      result[key] = null
    }
  }
  return result
}

// 手机号码脱敏
export const desensitizePhone = (phone: string) => {
  return phone.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2")
}

/**
 * 包装一个Promise工具，用于手动控制何时resolve
 */
/**
 * 创建一个可手动控制resolve的Promise
 * @returns 包含Promise和控制函数的对象
 */
export function createControlledPromise<T>(): {
  promise: Promise<T>
  resolve: (value: T | PromiseLike<T>) => void
  reject: (reason?: any) => void
} {
  let resolve: (value: T | PromiseLike<T>) => void
  let reject: (reason?: any) => void

  const promise = new Promise<T>((res, rej) => {
    resolve = res
    reject = rej
  })

  return {
    promise,
    resolve: resolve!,
    reject: reject!,
  }
}

/**
 * 获取URL中的参数值
 * @param name - 参数名
 * @param url - 完整的URL
 * @returns 参数值，如果未找到则返回undefined
 */
export function getUrlKey(name: string, url: string) {
  // 使用正则表达式判断，如果hash部分在search部分之前，即类似于http://localhost:8080/#/home?name=张三，则先把hash部分放置到后面
  // 使用正则表达式检查 hash 是否在 search 之前
  const hashBeforeSearchRegex = /^(.*?)(#[^?]*?)(\?.*)?$/
  const match = url.match(hashBeforeSearchRegex)
  let processedUrl = url
  if (match) {
    // 如果 hash 在 search 之前，重新排列 URL 部分
    processedUrl = `${match[1]}${match[3] || ""}${match[2]}`
  }
  const queryURL = new URL(decodeURIComponent(processedUrl))
  if (!queryURL) return undefined
  const params = queryURL.searchParams
  if (params.has(name)) {
    return params.get(name)
  }
  return undefined
}

// 检查组件类型并获取组件名称
export const getComponentName = (component: any): string | undefined => {
  if (typeof component === "function") {
    // 异步组件或生产环境
    return component.name
  } else if (component && typeof component === "object") {
    // 开发环境
    return component.__name || component.name
  }
  return undefined
}

/**
 * 获取当前环境
 * @returns 当前环境
 */
export function getEnv() {
  return import.meta.env.MODE as "development" | "staging" | "production" | "dev"
}

/**
 * 通过url获取模块类型
 * @param url 请求的url
 * @returns 模块类型 如：teashop_supplier、teashop_merchant、teashop_base
 */
export const getPrefixByUrl = (url: string) => {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    // 只提取协议后的路径部分
    const path = url.replace(/^https?:\/\/[^/]+/, "")
    return path.split("/")[1] ?? "unknown"
  } else if (url.startsWith("/")) {
    return url.split("/")[1] ?? 'unknown'
  } else {
    return url.split("/")[0] ?? 'unknown'
  }
}
